import { useQuery } from '@tanstack/react-query'
import { Cpu, MessageSquare, MoreHorizontal, Zap } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Timeline, TimelineGroup, TimelineItem } from '@/components/ui/timeline'
import { actionStatusTone, groupByDay, recordStatusTone } from '@/lib/hr'
import { EMPTY_VALUE, formatDateOnly, formatDateTime, formatTime } from '@/lib/format'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { cn } from '@/lib/utils'
import {
  hrApi,
  type HrAction,
  type HrHistoryEvent,
  type HrHistoryKind,
  type HrRecordKind,
} from '@/services/http/hr'
import { actionLabel, useCancelQueuedAction, useRunAction } from '@/features/hr/hr-actions'
import { formatLeaveBound } from '@/features/hr/request-value'
import { useAuthStore } from '@/stores/auth-store'

const t = messages.hr

/** Satırda kişi: baş harfli avatar (fotoğraf yok, Q7) + ad, altında sicil. */
export function PersonCell({ name, sub }: { name: string | null; sub?: string | null }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <Avatar name={name ?? '?'} size="sm" />
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="truncate">{name ?? t.unknown}</span>
        {sub && <span className="text-text-muted truncate text-xs">{sub}</span>}
      </span>
    </span>
  )
}

export function RecordStatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-text-muted">{EMPTY_VALUE}</span>
  return (
    <Badge tone={recordStatusTone(status)} size="sm">
      {t.recordStatus[status] ?? status}
    </Badge>
  )
}

/** Kaydın son panel aksiyonu: ne istendi ve durumu. */
export function ActionStatusBadge({ action }: { action: HrAction | undefined }) {
  if (!action) return <span className="text-text-muted">{EMPTY_VALUE}</span>
  return (
    <Badge
      tone={actionStatusTone(action.status)}
      size="sm"
      title={`${actionLabel(action.type)} · ${action.requested_by.name ?? ''}`}
    >
      {t.actionStatus[action.status] ?? action.status}
    </Badge>
  )
}

export function DetailGrid({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-sm">
      {items.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-text-muted">{label}</dt>
          <dd className="min-w-0 break-words">{value ?? EMPTY_VALUE}</dd>
        </div>
      ))}
    </dl>
  )
}

export interface ActionSpec {
  type: string
  /** Açıklama zorunlu (yoksay / elle girildi): diyalogda sorulur. */
  needsReason?: boolean
  /** Geri alınamaz: onay diyaloğu. */
  confirm?: { title: string; body: string }
  params?: Record<string, unknown>
  danger?: boolean
  /** Özel diyalog açan aksiyon (ör. izin saati): çağıran işler. */
  onSelect?: () => void
  /** Menünün genel izninden farklı izin isteyen aksiyon (ör. pasife alma). */
  permission?: string
}

/** Satırın aksiyon menüsü; izin anahtarı olmayan kullanıcıya hiç görünmez. */
export function RecordActions({
  kind,
  id,
  actions,
  permission = 'hr.action:run',
}: {
  kind: HrRecordKind
  id: string
  actions: ActionSpec[]
  permission?: string
}) {
  const hasPermission = useAuthStore((state) => state.hasPermission)
  const visible = actions.filter((spec) => hasPermission(spec.permission ?? permission))
  const run = useRunAction(kind, id)
  const cancelQueued = useCancelQueuedAction(kind, id)
  // Satırın son aksiyonu sıradaysa menü onu geri almayı da sunar (aynı sorgu satır rozetinde).
  const latest = useQuery({
    queryKey: queryKeys.hr.actions(kind, [id]),
    queryFn: () => hrApi.latestActions(kind, [id]),
    enabled: visible.length > 0,
    refetchInterval: (state) =>
      state.state.data?.some((a) => a.status === 'bekliyor') ? 4000 : false,
  })
  const queued = latest.data?.find((action) => action.status === 'bekliyor')
  const [pending, setPending] = useState<ActionSpec | null>(null)
  const [reason, setReason] = useState('')
  if (visible.length === 0) return null

  const select = (spec: ActionSpec) => {
    if (spec.onSelect) return spec.onSelect()
    if (spec.needsReason || spec.confirm) {
      setReason('')
      setPending(spec)
      return
    }
    run.mutate({ type: spec.type, params: spec.params })
  }
  const submit = () => {
    if (!pending) return
    run.mutate(
      {
        type: pending.type,
        params: { ...pending.params, ...(pending.needsReason ? { aciklama: reason.trim() } : {}) },
      },
      { onSuccess: () => setPending(null) },
    )
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={<Button size="sm" variant="secondary" disabled={run.isPending} />}
        >
          <MoreHorizontal />
          {t.actions.menu}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {visible.map((spec) => (
            <DropdownMenuItem
              key={spec.type}
              onClick={() => select(spec)}
              className={cn(spec.danger && 'text-danger')}
            >
              {actionLabel(spec.type)}
            </DropdownMenuItem>
          ))}
          {queued && (
            <DropdownMenuItem
              disabled={cancelQueued.isPending}
              onClick={() => cancelQueued.mutate(queued.id)}
            >
              {t.actions.cancelQueued}: {actionLabel(queued.type)}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pending?.confirm?.title ?? (pending && actionLabel(pending.type))}
            </DialogTitle>
            {pending?.confirm && <DialogDescription>{pending.confirm.body}</DialogDescription>}
          </DialogHeader>
          {pending?.needsReason && (
            <DialogBody className="flex flex-col gap-1.5">
              <Label htmlFor={`hr-reason-${id}`}>{t.actions.reason}</Label>
              <Textarea
                id={`hr-reason-${id}`}
                value={reason}
                maxLength={2000}
                placeholder={t.actions.reasonPlaceholder}
                onChange={(event) => setReason(event.target.value)}
              />
            </DialogBody>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPending(null)}>
              {t.actions.cancel}
            </Button>
            <Button
              variant={pending?.danger ? 'danger' : 'primary'}
              disabled={run.isPending || (pending?.needsReason === true && !reason.trim())}
              onClick={submit}
            >
              {t.actions.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

const SOURCE_ICONS = { sistem: Cpu, aksiyon: Zap, talep: MessageSquare } as const

const isEmpty = (value: unknown) => value === null || value === undefined || value === ''

function changeValue(field: string, value: unknown): string {
  if (isEmpty(value)) return t.history.empty
  if (field === 'hata_kategori' && typeof value === 'string') return t.category[value] ?? value
  if ((field === 'entrance_type' || field === 'giris_cikis') && typeof value === 'string') {
    return t.girisCikis[value] ?? value
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
    return formatDateTime(value)
  }
  // İzin talebinin yerel sınırı: "YYYY-MM-DD" ya da "YYYY-MM-DD HH:MM".
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}( \d{2}:\d{2})?$/.test(value)) {
    return formatLeaveBound(value)
  }
  if (typeof value === 'boolean') return value ? t.yes : t.no
  return typeof value === 'string' ? value : JSON.stringify(value)
}

/** Boş → boş değişiklikler (kayıt eklenirken dolmamış kolonlar) gösterilmez. */
function visibleChanges(event: HrHistoryEvent) {
  return (event.changes ?? []).filter((change) => !(isEmpty(change.old) && isEmpty(change.new)))
}

function eventTitle(event: HrHistoryEvent): string {
  if (event.source === 'sistem') {
    return ((t.history as Record<string, unknown>)[event.type] as string) ?? event.type
  }
  if (event.source === 'talep') return t.history.talep[event.type] ?? event.type
  const [base, step] = event.type.split('.')
  return step ? `${actionLabel(base)} · ${t.history.step(step)}` : actionLabel(base)
}

function eventTone(event: HrHistoryEvent) {
  if (event.source === 'sistem') return 'neutral' as const
  if (event.source === 'talep') {
    if (event.type === 'onaylandi') return 'success' as const
    if (event.type === 'iptal_edildi') return 'danger' as const
    if (event.type === 'revizyon_istendi') return 'warning' as const
    return 'info' as const
  }
  return actionStatusTone(event.status)
}

/** Kaydın tek zaman çizelgesi: sistem olayları + panel aksiyonları + talep yazışması. */
export function RecordHistory({ kind, id }: { kind: HrHistoryKind; id: string }) {
  const query = useQuery({
    queryKey: queryKeys.hr.history(kind, id),
    queryFn: () => hrApi.history(kind, id),
  })
  if (query.isLoading) return <Loading rows={3} />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  const history = query.data
  if (!history) return null
  return (
    <div className="flex flex-col gap-2">
      {!history.system_available && (
        <p className="text-warning text-xs">{t.detail.historyPartial}</p>
      )}
      <HistoryTimeline events={history.events} linkRequests={kind !== 'talep'} />
    </div>
  )
}

/** Hazır olay listesinin zaman çizelgesi (en yeni üstte, güne göre gruplu). */
export function HistoryTimeline({
  events,
  linkRequests = false,
}: {
  events: HrHistoryEvent[]
  /** Kaydın geçmişinde talep olayından talebe bağlantı (talebin kendi çizelgesinde yok). */
  linkRequests?: boolean
}) {
  return events.length === 0 ? (
    <p className="text-text-muted text-sm">{t.detail.historyEmpty}</p>
  ) : (
    <Timeline aria-label={t.detail.history}>
      {groupByDay(events).map((group) => (
        <TimelineGroup key={group.day} label={formatDateOnly(group.day)} headingLevel={4}>
          {group.events.map((event, index) => {
            const Icon = SOURCE_ICONS[event.source]
            return (
              <TimelineItem
                key={`${event.at}-${index}`}
                icon={<Icon className="size-3.5" />}
                tone={eventTone(event)}
                title={
                  <span className="flex flex-wrap items-center gap-1.5">
                    {eventTitle(event)}
                    {event.status && event.source === 'aksiyon' && (
                      <Badge tone={actionStatusTone(event.status)} size="sm">
                        {t.actionStatus[event.status] ?? event.status}
                      </Badge>
                    )}
                  </span>
                }
                time={formatTime(event.at)}
                actor={
                  event.actor?.name ? (
                    <span className="flex items-center gap-1.5">
                      <Avatar name={event.actor.name} size="sm" />
                      {event.actor.name}
                    </span>
                  ) : event.source === 'sistem' ? (
                    <span className="text-text-muted">{t.system}</span>
                  ) : undefined
                }
              >
                {visibleChanges(event).length > 0 && (
                  <ul className="text-text-secondary flex flex-col gap-0.5 text-xs">
                    {visibleChanges(event).map((change) => (
                      <li key={change.field}>
                        <span className="text-text-muted">
                          {t.history.field[change.field] ?? change.field}:
                        </span>{' '}
                        {/* Panel olayında eski değer yoksa (talep açılışı) yalnız istenen değer. */}
                        {isEmpty(change.old) && event.source !== 'sistem'
                          ? changeValue(change.field, change.new)
                          : `${changeValue(change.field, change.old)} → ${changeValue(change.field, change.new)}`}
                      </li>
                    ))}
                  </ul>
                )}
                {event.message && (
                  <p className="text-text-secondary text-xs whitespace-pre-wrap">{event.message}</p>
                )}
                {linkRequests && event.source === 'talep' && event.request_id && (
                  <Link
                    to={`/ik/onaylar?talep=${event.request_id}`}
                    className="text-brand text-xs underline-offset-2 hover:underline"
                  >
                    {t.approvals.openRequest}
                  </Link>
                )}
              </TimelineItem>
            )
          })}
        </TimelineGroup>
      ))}
    </Timeline>
  )
}

/** Açılan satırın iskeleti: solda ayrıntı + aksiyonlar, sağda geçmiş. */
export function SubRowLayout({ detail, history }: { detail: ReactNode; history: ReactNode }) {
  return (
    <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
      <div className="flex min-w-0 flex-col gap-3">{detail}</div>
      <section className="flex min-w-0 flex-col gap-2">
        <h3 className="text-sm font-medium">{t.detail.history}</h3>
        <div className="max-h-96 overflow-y-auto [--timeline-surface:var(--surface-muted)]">
          {history}
        </div>
      </section>
    </div>
  )
}
